import {_} from "lodash";
import * as ko from "knockout";
import {getAnyValue, getNextId} from "../../util/misc";
import {IDialogDefinition, IDialogOptionsComponent, IDialogOptionsConfirm} from "./index";
import {IKnockoutComponent, TBaseOrObservableType} from "../../index";
import pubsub from "../../pubsub";
import managerHtml from './manager.html?raw';

export let template = managerHtml;

export interface IDialogOpt {
    close?: () => void // closes the dialog - populated by this manager
    cssCls?: string
    id?: string // id of the dialog element - populated by this manager
    isClosable?: KnockoutObservable<boolean>
    callbackFn?: (...args: any[]) => void // if given this method is called from a dialog component (when a operation is executed)
    width?: TBaseOrObservableType<string>
}

export interface IKnockoutComponentDialogContent extends IKnockoutComponent {
    params?: IKnockoutComponent['params'] & {
        __dialog?: IDialogOpt
    }
}

type TDialogComponent = IDialogDefinition | IKnockoutComponentDialogContent

export class DialogManager {

    private readonly dialogs: KnockoutObservableArray<TDialogComponent> = ko.observableArray<TDialogComponent>([]);
    private readonly width: KnockoutObservable<string | null> = ko.observable(null)
    private readonly dialogIds: KnockoutObservableArray<string> = ko.observableArray<string>([])

    constructor() {
        window.addEventListener("popstate", (e: PopStateEvent) => {
            this.dialogs().forEach((diag: TDialogComponent) => {
                const isClosable = diag.params.__dialog?.isClosable ?? diag.params.isClosable;
                if (ko.unwrap(isClosable)) {
                    this.removeComponent(diag)
                }
            });
        }, true);

        // This bugfix makes sure that bootstrap doesn't release the overflow-x:hidden on the body when a dialog is
        // removed (and there is still dialogs present in the DOM). Instead we'll force the 'modal-open' class back onto
        // the body as long as there are dialogs in the array.
        this.dialogs.subscribe(arr => {
            requestAnimationFrame(() => {
                if (arr.length) {
                    const htmlBodyElement = document.querySelector('body');
                    if (htmlBodyElement && !htmlBodyElement.classList.value.includes('modal-open')) {
                        htmlBodyElement.classList.add('modal-open')
                    }
                }
            })
        })
    }

    private add(dialogDefinition: IDialogDefinition) {
        const id = DialogManager.getNextId();
        this.dialogIds.push(id)

        // Caller dismiss hook (backdrop / Escape / X / Cancel all end in hidden.bs.modal).
        const onDismiss = dialogDefinition.params?.doClose;

        const dd: IDialogDefinition = _.defaultsDeep(dialogDefinition, {
            // name: null,
            params: {
                // dialogContent: null,
                doClose: () => this.remove(id),
                fullWidth: true,
                id: id,
                isClosable: ko.observable(true),
            }
        });

        this.dialogs.push(dd);

        const init = () => {
            const element = document.querySelector('#' + id) as HTMLElement;
            if (element) {
                const closable = ko.unwrap(dd.params.isClosable ?? true);
                this.setDialogAttr(id, {
                    show: true,
                    backdrop: closable ? true : 'static',
                    keyboard: closable,
                });
                this.getDialogElement(id).on('hidden.bs.modal', () => {
                    onDismiss?.();
                    this.remove(id);
                })
            } else {
                requestAnimationFrame(() => init())
            }
        };

        init();

        return id
    }

    addCustom(component: IKnockoutComponentDialogContent) {
        const id = DialogManager.getNextId();
        this.dialogIds.push(id)

        component.params = _.defaultsDeep(component.params ?? {}, {
            __dialog: {
                close: () => this.remove(id),
                id,
                isClosable: ko.observable(true),
                type: 'custom',
                width: ko.observable(null),
            }
        })
        this.dialogs.push(component)

        const closable = ko.unwrap(component.params.__dialog.isClosable);
        this.setDialogAttr(id, {
            show: true,
            backdrop: closable ? true : 'static',
            keyboard: closable,
        });
        this.getDialogElement(id).on('hidden.bs.modal', (e) => {
            this.remove(id);
        })

        return id
    }

    addComponent(options: IDialogOptionsComponent) {
        const dd: IDialogDefinition = {
            name: 'component/dialog/component/basic',
            params: options,
        };
        if (!ko.isObservable(options.title)) {
            dd.params.title = ko.observable(options.title) as unknown as KnockoutObservable<string>
        }
        dd.params.content.params.dialog = options;
        return this.add(dd);
    }

    addConfirm(options: IDialogOptionsConfirm) {
        this.add({
            name: 'component/dialog/confirm/basic',
            params: _.defaultsDeep(options, {
                fullWidth: false,
            }),
        });
    }

    remove(dialogId: string, timeout = 300) {
        this.setDialogAttr(dialogId, 'hide');
        setTimeout(() => {
            const dialog = this.dialogs().find(dialogDefinition => (dialogDefinition.params.id ?? dialogDefinition.params.__dialog?.id) == dialogId);
            if (dialog) {
                this.dialogs.remove(dialog);
            }
            pubsub.trigger('after.remove.dialog')
            this.dialogIds.remove(dialogId)
        }, timeout);
    }

    removeAll(timeout?: number) {
        this.dialogIds().forEach((dialogId) => {
            this.remove(dialogId, timeout)
        })
    }

    private removeComponent(component: TDialogComponent) {
        const id = component.params.__dialog?.id ?? component.params.id
        this.remove(id)
    }

    onCloseClick(component: TDialogComponent) {
        this.remove(component.params.id);
    }

    private getDialogElement(id: string): JQuery<HTMLElement> {
        return jQuery('#' + id);
    }

    private setDialogAttr(id: string, attr: any) {
        this.getDialogElement(id).modal(attr);
    }

    private static getNextId(): string {
        return 'dialog-manager-' + getNextId();
    }

}
